import React, { useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useLegalStatus } from '../hooks/useLegalStatus';
import { LegalAcceptanceModal } from './LegalAcceptanceModal';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, signOut } = useAuth();
  const location = useLocation();
  const [recheckCounter, setRecheckCounter] = useState(0);
  const legalStatus = useLegalStatus({ enabled: isAuthenticated && !isLoading });

  const handleAcceptanceComplete = useCallback(() => {
    legalStatus.invalidate();
    setRecheckCounter(c => c + 1); // trigger re-render to re-run effect
  }, [legalStatus]);

  // Suppress lint warning — recheckCounter used to trigger re-render after invalidation
  void recheckCounter;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (legalStatus.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (legalStatus.error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ maxWidth: 480, textAlign: 'center', p: 3 }}>
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            Unable to load your account. Your account setup may be incomplete.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If you recently signed up, please try creating your account again.
            If this problem persists, contact support at{' '}
            <a href="mailto:support@artlegacyinstitute.org">support@artlegacyinstitute.org</a>.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={signOut}>
              Sign Out
            </Button>
            <Button variant="contained" href="/signup">
              Create Account
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (legalStatus.needsAcceptance.length > 0) {
    return (
      <LegalAcceptanceModal
        needsAcceptance={legalStatus.needsAcceptance}
        currentVersions={legalStatus.currentVersions}
        onComplete={handleAcceptanceComplete}
      />
    );
  }

  return <>{children}</>;
};
